import { t } from "i18next";
import { get, indexOf, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { setLoading } from "../../../redux/ducks/Loading";
import { useDispatch, useSelector } from "react-redux";
import { formatNumber, showError, showSuccess } from "../../../utils/utilities";
import { Checkbox, Col, Input, Modal, Row, Spin, Table } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setSection } from "../../../redux/ducks/Section";
import TimesheetApprovalApi from "../../../services/api/TimesheetApprovalApi";

const TimesheetApprovalDetailRespCDC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const CID = useParams().id;
  const navigate = useNavigate();
  const [state, setState] = useState({
    tableData: [],
    dynamicForm: [],
    modal: false,
  });

  useEffect(() => {
    if (CID) {
      dispatch(setSection({ section: { title: location.state?.name } }));
    }
    getData();
  }, []);

  const getData = async () => {
    dispatch(setLoading(true));
    const table = await TimesheetApprovalApi.getTimesheetApprovalEmployee(CID);
    if (!table.error) {
      const dynamicFormData = [];
      table.data.forEach((cc, i) => {
        cc.key = i;
        dynamicFormData.push(cc);
      });
      setState((prevState) => ({
        ...prevState,
        tableData: table.data,
        dynamicForm: dynamicFormData,
      }));
    } else {
      showError(get(table, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleCheckbox = (event, data) => {
    let newData = state.dynamicForm.find((item) => item.key === data.key);
    newData.cid = event.target.checked;
    setState((prevState) => ({
      ...prevState,
      dynamicForm: [...prevState.dynamicForm.filter((item) => item.key !== data.key), newData],
    }));
  };

  const handleNotes = (event, data) => {
    let newData = state.dynamicForm.find((item) => item.key === data.key);
    newData.pmNotes = event.target.value;
    setState((prevState) => ({
      ...prevState,
      dynamicForm: [...prevState.dynamicForm.filter((item) => item.key !== data.key), newData],
    }));
  };

  const getNoteValue = (key) => {
    return state.dynamicForm.find((item) => item.key === key).pmNotes;
  };

  const columns = [
    { title: t("timesheet.approval.table.projectTaskId"), dataIndex: "projectTaskId", key: "projectTaskId" }, // Project
    { title: t("timesheet.approval.table.projectTaskName"), dataIndex: "projectTaskName", key: "projectTaskName" }, // Description activity
    { title: t("timesheet.approval.table.projectName"), dataIndex: "projectName", key: "projectName" }, // Description
    {
      title: t("timesheet.approval.monthAmount"), // ore totali = approvate + da approvare
      key: "monthAmountTotal",
      render: (record) =>
        formatNumber({
          value: record.monthAmountApproved + record.monthAmount,
          suffix: "HOUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    },
    {
      title: t("timesheet.approval.monthAmountApproved"),
      dataIndex: "monthAmountApproved", // ore già approvate
      key: "monthAmountApproved",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.approval.monthAmountWaitingApproval"),
      dataIndex: "monthAmount", // ore da approvare
      key: "monthAmountWaitingApproval",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.approval.table.monthWork"),
      dataIndex: "monthWork",
      key: "monthWork",
      render: (amount) => formatNumber({ value: amount, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.approval.table.pmNotesRespCDC"),
      dataIndex: "pmNotes",
      key: "pmNotes",
      width: 200,
      render: (_, record) => (
        <div>
          <Input onChange={(event) => handleNotes(event, record)} value={getNoteValue(record.key)} />
        </div>
      ),
    },
    {
      title: t("timesheet.approval.table.cid"),
      dataIndex: "cid",
      key: "cid",
      width: 100,
      render: (_, record) => <Checkbox onChange={(event) => handleCheckbox(event, record)} defaultChecked />,
    },
  ];

  const submitApproval = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const payload = [];
    state.dynamicForm.forEach((item) => {
      payload.push({
        timesheetPortalId: item.timesheetPortalId,
        pmNotes: item.pmNotes,
        status: item.cid ? "APPROVED" : "REFUTED",
      });
    });
    const submitResponse = await TimesheetApprovalApi.submitTimesheetApproval(CID, payload);
    if (!submitResponse.error) {
      showSuccess();
      closeModal();
      navigate("/timesheet-approval");
    } else {
      showError(get(submitResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const openModal = () => {
    setState((prevState) => ({ ...prevState, modal: true }));
  };

  const closeModal = () => {
    setState((prevState) => ({ ...prevState, modal: false }));
  };

  const summaryApprovedBlock = () => {
    let approved = [];
    state.dynamicForm?.forEach((row) => {
      if (row.cid) approved.push(row);
    });
    if (approved.length) {
      return (
        <>
          <Row>
            <Col span={24}>
              <div className="approved-list-title fw-bold approvalSubtitle">
                {t("timesheet.approval.modal.approved")}
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.project")}</div>
            </Col>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.activity")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.monthWork")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.hours")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.name")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.surname")}</div>
            </Col>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.pmNotesRespCDC")}</div>
            </Col>
          </Row>
          {approved.map((timesheet) => {
            return (
              <Row key={timesheet.key}>
                <Col span={4}>{timesheet.projectTaskId}</Col>
                <Col span={4}>{timesheet.projectTaskName}</Col>
                <Col span={3}>{formatNumber({ value: timesheet.monthWork, lang: currentLanguage, suffix: "EUR" })}</Col>
                <Col span={3}>
                  {formatNumber({ value: timesheet.monthAmount, lang: currentLanguage, suffix: "HOUR" })}
                </Col>
                <Col span={3}>{timesheet.name}</Col>
                <Col span={3}>{timesheet.surname}</Col>
                <Col span={4}>{timesheet.pmNotes}</Col>
              </Row>
            );
          })}
        </>
      );
    }
  };

  const summaryRefusedBlock = () => {
    let refused = [];
    state.dynamicForm?.forEach((row) => {
      if (!row.cid) {
        refused.push(row);
      }
    });
    if (refused.length) {
      return (
        <div style={{ paddingTop: 20 }}>
          <Row>
            <Col span={24}>
              <div className="PFred fw-bold approvalSubtitle">{t("timesheet.approval.modal.refused")}</div>
            </Col>
          </Row>
          <Row>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.project")}</div>
            </Col>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.activity")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.monthWork")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.hours")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.name")}</div>
            </Col>
            <Col span={3}>
              <div className="fw-bold">{t("timesheet.approval.modal.surname")}</div>
            </Col>
            <Col span={4}>
              <div className="fw-bold">{t("timesheet.approval.modal.pmNotesRespCDC")}</div>
            </Col>
          </Row>
          {refused.map((timesheet) => {
            return (
              <Row key={timesheet.key}>
                <Col span={4}>{timesheet.projectTaskId}</Col>
                <Col span={4}>{timesheet.projectTaskName}</Col>
                <Col span={3}>{formatNumber({ value: timesheet.monthWork, lang: currentLanguage, suffix: "EUR" })}</Col>
                <Col span={3}>
                  {formatNumber({ value: timesheet.monthAmount, lang: currentLanguage, suffix: "HOUR" })}
                </Col>
                <Col span={3}>{timesheet.name}</Col>
                <Col span={3}>{timesheet.surname}</Col>
                <Col span={4}>{timesheet.pmNotes}</Col>
              </Row>
            );
          })}
        </div>
      );
    }
  };

  const disableConfirm = () => {
    let check = true;
    state.dynamicForm.forEach((df) => {
      if (!df.cid && indexOf([null, undefined, ""], df.pmNotes) !== -1) {
        check = false;
      }
    });
    return !check;
  };

  return (
    <>
      {!isEmpty(state.tableData) && (
        <Table
          columns={columns}
          dataSource={state.tableData}
          expandable={{ defaultExpandAllRows: true }}
          sticky
          bordered
          scroll={true}
          pagination={false}
          size={"middle"}
        />
      )}
      <Row type="flex" align="middle" justify="end" className="mt-10">
        <Button
          currentIcon={<CheckOutlined className="icon-medium" />}
          clickCallback={openModal}
          isDisabled={disableConfirm()}
        />
      </Row>
      <Modal
        title={t("timesheet.approval.modal.title")}
        open={state.modal}
        width={900}
        onOk={closeModal}
        onCancel={closeModal}
        footer={[
          <Button
            key="timesheet-approval"
            currentIcon={<CheckOutlined className="icon-medium" />}
            clickCallback={() => submitApproval()}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        {summaryApprovedBlock()}
        {summaryRefusedBlock()}
      </Modal>
    </>
  );
};

export default TimesheetApprovalDetailRespCDC;
